import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Calendar, Plus, Trash2, X, CheckCircle2, AlertCircle } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * Organizer-side slot manager for an Experience event.
 * Lets the host create date+time slots with capacity and per-guest price.
 * Bookings decrement `available`; the manager shows real-time status.
 */
export default function ExperienceSlotManager({ eventId, token }) {
  const [slots, setSlots] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({ date: '', start_time: '', end_time: '', capacity: 10, price_cents: 0 });

  const fetchSlots = async () => {
    if (!eventId) return;
    setLoading(true);
    try {
      const res = await axios.get(`${API}/experiences/${eventId}/slots?include_past=true`);
      setSlots(res.data?.slots || []);
    } catch {
      setSlots([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchSlots(); }, [eventId]);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.date || !form.start_time) {
      toast.error('Date and start time are required');
      return;
    }
    try {
      await axios.post(
        `${API}/experiences/${eventId}/slots`,
        {
          ...form,
          capacity: Number(form.capacity) || 1,
          price_cents: Math.round(parseFloat(form.price_cents || 0) * 100),
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('Slot added');
      setShowForm(false);
      setForm({ date: '', start_time: '', end_time: '', capacity: 10, price_cents: 0 });
      fetchSlots();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to create slot');
    }
  };

  const remove = async (slotId) => {
    try {
      await axios.delete(`${API}/experiences/slots/${slotId}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      toast.success('Slot deleted');
      fetchSlots();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to delete slot');
    }
  };

  if (!eventId) {
    return (
      <div className="rounded-xl border border-amber-500/30 bg-amber-500/5 p-4 flex items-start gap-3">
        <AlertCircle className="h-5 w-5 text-amber-500 flex-shrink-0 mt-0.5" />
        <div className="text-sm">
          <p className="font-semibold">Save the experience first</p>
          <p className="text-xs text-muted-foreground">Once you save the basics, come back here to add time slots.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-3" data-testid="experience-slot-manager">
      <div className="flex items-center justify-between">
        <div>
          <Label className="text-sm font-semibold">Time Slots ({slots.length})</Label>
          <p className="text-[11px] text-muted-foreground">Each slot is a bookable departure. Guests pick a slot and a number of spots.</p>
        </div>
        <Button type="button" size="sm" onClick={() => setShowForm(v => !v)} data-testid="slot-mgr-add-btn">
          <Plus className="h-3 w-3 mr-1" /> Add slot
        </Button>
      </div>

      {showForm && (
        <form onSubmit={submit} className="rounded-lg border p-3 space-y-3 bg-muted/20" data-testid="slot-mgr-form">
          <div className="grid sm:grid-cols-5 gap-2">
            <div className="space-y-1">
              <Label className="text-[11px]">Date *</Label>
              <Input type="date" value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} data-testid="slot-date" />
            </div>
            <div className="space-y-1">
              <Label className="text-[11px]">Start *</Label>
              <Input type="time" value={form.start_time} onChange={(e) => setForm({ ...form, start_time: e.target.value })} data-testid="slot-start" />
            </div>
            <div className="space-y-1">
              <Label className="text-[11px]">End</Label>
              <Input type="time" value={form.end_time} onChange={(e) => setForm({ ...form, end_time: e.target.value })} />
            </div>
            <div className="space-y-1">
              <Label className="text-[11px]">Capacity *</Label>
              <Input type="number" min={1} value={form.capacity} onChange={(e) => setForm({ ...form, capacity: e.target.value })} data-testid="slot-capacity" />
            </div>
            <div className="space-y-1">
              <Label className="text-[11px]">Price / spot ($)</Label>
              <Input type="number" min={0} step="0.01" value={form.price_cents} onChange={(e) => setForm({ ...form, price_cents: e.target.value })} data-testid="slot-price" />
            </div>
          </div>
          <div className="flex gap-2 justify-end">
            <Button type="button" variant="ghost" size="sm" onClick={() => setShowForm(false)}>Cancel</Button>
            <Button type="submit" size="sm" data-testid="slot-save-btn">Save slot</Button>
          </div>
        </form>
      )}

      {loading ? (
        <p className="text-xs text-muted-foreground">Loading…</p>
      ) : slots.length === 0 ? (
        <div className="rounded-lg border border-dashed p-4 text-center text-sm text-muted-foreground">
          <Calendar className="h-6 w-6 mx-auto mb-1 opacity-60" />
          No slots yet. Add one to start accepting bookings.
        </div>
      ) : (
        <div className="space-y-1.5">
          {slots.map(s => {
            const isFull = s.status === 'sold_out';
            return (
              <div key={s.slot_id} className="flex items-center justify-between gap-2 p-2 rounded-lg border bg-background" data-testid={`slot-row-${s.slot_id}`}>
                <div className="flex items-center gap-2.5 min-w-0">
                  <div className={`p-1.5 rounded ${isFull ? 'bg-rose-500/10 text-rose-500' : 'bg-emerald-500/10 text-emerald-500'}`}>
                    {isFull ? <X className="h-3 w-3" /> : <CheckCircle2 className="h-3 w-3" />}
                  </div>
                  <div className="text-sm">
                    <div className="font-medium">{s.date} · {s.start_time}{s.end_time ? `–${s.end_time}` : ''}</div>
                    <div className="text-[11px] text-muted-foreground">
                      {s.booked_count}/{s.capacity} booked
                      {s.price_cents > 0 && <> · ${(s.price_cents / 100).toFixed(2)}/spot</>}
                    </div>
                  </div>
                </div>
                <Button type="button" variant="ghost" size="icon" className="text-muted-foreground h-7 w-7" onClick={() => remove(s.slot_id)} data-testid={`slot-delete-${s.slot_id}`} disabled={s.booked_count > 0}>
                  <Trash2 className="h-3.5 w-3.5" />
                </Button>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
